import React, { useMemo, useState } from "react";
import Container from "../ui/Container";
import Button from "../ui/Button";
import { BRAND, PRICING } from "../../lib/config";
import {
  Server,
  Cpu,
  HardDrive,
  DatabaseBackup,
  Shield,
  Rocket,
  Calendar,
  Tag,
  List,
  Check,
  XIcon,
  Users,
  Split,
} from "lucide-react";

function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-500/10 ring-1 ring-cyan-400/20">
          <Icon className="h-4 w-4 text-cyan-300" />
        </div>
        <div className="text-sm text-white/70">{label}</div>
      </div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

function CyclePill({ active, onClick, label, sublabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex-1 rounded-2xl border px-4 py-3 text-left transition",
        active
          ? "border-cyan-300/40 bg-cyan-500/10"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]",
      ].join(" ")}
    >
      <div className="text-sm font-semibold text-white">{label}</div>
      {sublabel ? (
        <div className="mt-1 text-xs text-white/55">{sublabel}</div>
      ) : null}
    </button>
  );
}

function PlanPill({ active, price, onClick, label, players, cpu, ram, disk, upgradable, mostPopular }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex-1 rounded-2xl border px-4 py-4 text-left transition cursor-pointer",
        active
          ? "border-cyan-300/40 bg-cyan-500/10"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            {label}
            {mostPopular ? (
              <span className="rounded-full border border-cyan-300/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-200">
                Popular
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-xs text-white/55">
            {label === "Starter" ? "Perfect for small servers" : null}
            {label === "Standard" ? "Great for growing servers" : null}
            {label === "Professional" ? "Ideal for larger communities" : null}
            {label === "Network" ? "Built for multi-server setups" : null}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-lg font-semibold text-white">£{price}</div>
          <div className="text-xs text-white/55">/month</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-white/70">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <span className="text-white/45">RAM</span>
          <div className="mt-1 font-semibold text-white">{ram} GB</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <span className="text-white/45">CPU</span>
          <div className="mt-1 font-semibold text-white">{cpu}%</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <span className="text-white/45">Disk</span>
          <div className="mt-1 font-semibold text-white">{disk} GB NVMe</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <span className="text-white/45">Players</span>
          <div className="mt-1 font-semibold text-white">{players}</div>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <div className="flex items-center gap-2 text-xs text-white/70">
          <Check className="h-4 w-4 text-green-400" />
          <span>Unlimited bandwidth</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70">
          <Check className="h-4 w-4 text-green-400" />
          <span>24/7 support</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70">
          <Check className="h-4 w-4 text-green-400" />
          <span>DDoS protection included</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70">
          <Check className="h-4 w-4 text-green-400" />
          <span>
            {label === "Starter" ? "2 server splits included" : "50GB+ NVMe storage included"}
          </span>
        </div>
        {upgradable ? (
          <>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <Check className="h-4 w-4 text-green-400" />
              <span>RAM upgrades available</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <Check className="h-4 w-4 text-green-400" />
              <span>CPU upgrades available</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <XIcon className="h-4 w-4 text-red-400" />
              <span>RAM upgrades unavailable</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <XIcon className="h-4 w-4 text-red-400" />
              <span>CPU upgrades unavailable</span>
            </div>
          </>
        )}
      </div>
    </button>
  );
}

function moneyGBP(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export default function PricingCalculator() {
  const mcPlans = [
    {
      id: "starter",
      label: "Starter",
      ram: 3,
      cpu: 200,
      disk: 20,
      players: "5 - 10",
      price: 3.99,
      url: `${BRAND.deployUrl}game-hosting/starter/checkout?plan=`,
      cycles: {"m": 279, "q": 280, "h": 281, "y": 282},
      upgradable: false,
      mostPopular: false,
    },
    {
      id: "standard",
      label: "Standard",
      ram: 6,
      cpu: 300,
      disk: 50,
      players: "10 - 25",
      price: 7.49,
      url: `${BRAND.deployUrl}game-hosting/standard/checkout?plan=`,
      cycles: {"m": 283, "q": 284, "h": 285, "y": 286},
      upgradable: false,
      mostPopular: true,
    },
    {
      id: "pro",
      label: "Professional",
      ram: 8,
      cpu: 400,
      disk: 80,
      players: "20 - 35",
      price: 9.99,
      url: `${BRAND.deployUrl}game-hosting/professional/checkout?plan=`,
      cycles: {"m": 287, "q": 288, "h": 289, "y": 290},
      upgradable: false,
      mostPopular: false,
    },
    {
      id: "network",
      label: "Network",
      ram: 12,
      cpu: 600,
      disk: 120,
      players: "30 - 60",
      price: 14.49,
      url: `${BRAND.deployUrl}game-hosting/network/checkout?plan=`,
      cycles: {"m": 291, "q": 292, "h": 293, "y": 294},
      upgradable: true,
      mostPopular: false,
    },
  ];

  const [planId, setPlanId] = useState("standard");

  const selectedPlan = useMemo(
    () => mcPlans.find((p) => p.id === planId) || mcPlans[0],
    [planId]
  );

  const cycles = [
    { id: "m", months: 1, discount: 0.0, label: "Monthly", sublabel: "No discount", },
    { id: "q", months: 3, discount: 0.05, label: "3 months", sublabel: "Save 5%" },
    { id: "h", months: 6, discount: 0.075, label: "6 months", sublabel: "Save 7.5%" },
    { id: "y", months: 12, discount: 0.0, label: "12 months", sublabel: "Best value • 1 month free" },
  ];

  const [cycleId, setCycleId] = useState("m");

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === cycleId) || cycles[0],
    [cycleId]
  );

  const computed = useMemo(() => {
    const monthlyRaw = selectedPlan.price;
    const months = selectedCycle.months;

    let originalRaw = monthlyRaw * months;
    let prepaidRaw = originalRaw;

    if (selectedCycle.id === "y") {
      // 1 month free → charge 11 months instead of 12
      prepaidRaw = monthlyRaw * (months - 1);
    } else {
      const discount = selectedCycle.discount;
      prepaidRaw = originalRaw * (1 - discount);
    }

    const savingsRaw = originalRaw - prepaidRaw;

    const url = selectedPlan.url;
    const cycles = selectedPlan.cycles;

    return {
      ...selectedPlan,
      monthlyRaw,
      months,
      url,
      cycles,
      originalRaw,
      prepaidRaw,
      savingsRaw,
      displayMonthly: `£${monthlyRaw}/mo`,
      displayDueToday:
        months === 1
          ? `£${monthlyRaw}/mo`
          : `£${prepaidRaw.toFixed(2)} due today`,
      displaySavings:
        months === 1
          ? null
          : `Save £${savingsRaw.toFixed(2)} over ${months} months`,
    };
  }, [selectedPlan, selectedCycle]);

  const addonRows = selectedPlan.id === "network"
    ? [
        {
          title: "Extra RAM",
          desc: "Scale your Network plan without migrating server files or changing plan tiers.",
          price: "£1.20 / GB",
        },
        {
          title: "Extra CPU",
          desc: "Add more CPU allocation for larger networks and busier peak times.",
          price: "From £0.50 / +1 vCore",
        },
        {
          title: "Extra Storage",
          desc: "Add extra NVMe disk beyond your included allocation.",
          price: "£2 / 10GB",
        },
        {
          title: "Extra Splits",
          desc: "2 server splits included as standard. Add more whenever you need them.",
          price: "£1 / 10 splits",
        },
      ]
    : [
        {
          title: "Extra Storage",
          desc: "Add extra NVMe disk beyond your included allocation.",
          price: "£2 / 10GB",
        },
        {
          title: "Extra Splits",
          desc: "2 server splits included as standard. Add more whenever you need them.",
          price: "£1 / 10 splits",
        },
      ];

  return (
    <section id="pricing" className="bg-[#070A0F] py-20 scroll-mt-20">
      <Container>
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-xs font-semibold tracking-wider text-white/50">
            PRICING
          </div>

          <h2 className="mt-3 text-balance text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Choose your plan
            <span className="block">
              We handle the rest
              <span className="text-cyan-300">.</span>
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-pretty text-base leading-7 text-white/70 sm:text-lg">
            Ryzen 7000 game hosting with clear plans, NVMe storage, and instant scaling on Network.
          </p>
        </div>

        <div className="mt-14">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-xs font-semibold tracking-wider text-white/50">CONFIGURE</div>
                <div className="mt-2 text-lg font-semibold text-white">Game Server</div>
              </div>

              <div className="text-right">
                <div className="text-xs text-white/50">From</div>
                <div className="mt-1 text-3xl font-semibold text-white">
                  {computed.displayMonthly}
                  <span className="text-cyan-300">.</span>
                </div>
              </div>
            </div>

            <div className="mt-7">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <List className="h-4 w-4 text-cyan-300" />
                  Hosting Plan
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
                {mcPlans.map((p) => (
                  <PlanPill
                    key={p.id}
                    active={planId === p.id}
                    price={p.price}
                    onClick={() => setPlanId(p.id)}
                    label={p.label}
                    players={p.players}
                    cpu={p.cpu}
                    ram={p.ram}
                    disk={p.disk}
                    upgradable={p.upgradable}
                    mostPopular={p.mostPopular}
                  />
                ))}
              </div>
            </div>

            {computed.id === "network" && (
              <div className="mt-4 rounded-2xl border border-cyan-300/20 bg-cyan-500/5 p-4">
                <div className="text-xs font-semibold tracking-wider text-cyan-200/80">
                  NETWORK UPGRADES
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-sm text-white/75">Extra RAM</div>
                    <div className="text-sm font-semibold text-white">£1.20 / GB</div>
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-sm text-white/75">Extra CPU</div>
                    <div className="text-sm font-semibold text-white">from £0.50 / +100%</div>
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-sm text-white/75">Extra NVMe Storage</div>
                    <div className="text-sm font-semibold text-white">£2 / 10GB</div>
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-sm text-white/75">Extra Server Splits (10 as standard on network plan)</div>
                    <div className="text-sm font-semibold text-white">£1 / 10 splits</div>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-7">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Calendar className="h-4 w-4 text-cyan-300" />
                  Billing cycle
                </div>
                {computed.displaySavings ? (
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-white/80">
                    <Tag className="h-4 w-4 text-cyan-300" />
                    {computed.displaySavings}
                  </div>
                ) : (
                  <div className="text-xs text-white/45">Pay as you go</div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {cycles.map((c) => (
                  <CyclePill
                    key={c.id}
                    active={cycleId === c.id}
                    onClick={() => setCycleId(c.id)}
                    label={c.label}
                    sublabel={c.sublabel}
                  />
                ))}
              </div>

              <div className="mt-4 text-sm text-white/70">
                <span className="text-white/50">Total:</span>{" "}
                <span className="font-semibold text-white">{computed.displayDueToday}</span>

                {computed.months > 1 && (
                  <span className="ml-2 text-xs text-white/45">
                    (Was: {moneyGBP(computed.originalRaw)})
                  </span>
                )}
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <StatRow icon={Cpu} label="CPU (Pterodactyl)" value={`${computed.cpu}%`} />
              <StatRow icon={Server} label="RAM" value={`${computed.ram} GB`} />
              <StatRow icon={HardDrive} label="NVMe Disk" value={`${computed.disk} GB`} />
              <StatRow icon={Users} label="Recommended Players" value={computed.players} />
              <StatRow
                icon={DatabaseBackup}
                label="Backups"
                value={`${PRICING?.backups?.daily ?? 1} daily + ${PRICING?.backups?.manual ?? 1} manual`}
              />
              <StatRow icon={Shield} label="DDoS Protection" value="Included" />
              <StatRow icon={Split} label="Server Splits" value="2 included" />
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                href={`${computed.url}${computed.cycles[cycleId]}`}
                className="w-full sm:w-auto"
              >
                Deploy this Server <Server className="h-4 w-4 opacity-80" />
              </Button>
              <Button variant="secondary" href={BRAND.gameHostingUrl} className="w-full sm:w-auto">
                Learn more <Rocket className="h-4 w-4 opacity-70" />
              </Button>
            </div>

            <p className="mt-4 text-xs leading-5 text-white/55">
              Player recommendations assume Paper-based servers with moderate plugins and sensible view distance.
              <span className="text-white/30"> Upgrades are instant and prorated where available.</span>
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}